import type {
  DomainEntityConfiguration,
  ConfigEntityConfiguration,
  ConfigEntity,
  DomainEntity,
  EntityOperationConfig,
  OperationType,
} from '../../types/configuration';
import {
  isConfigEntity,
  isConfigEntityConfiguration,
  isDomainEntity,
} from '../is-entity/is-entity';
import { operationId } from './operation-id';

const hasEndpointForOperation = (
  endpoints: object | undefined,
  operation: OperationType,
): boolean =>
  Boolean(
    endpoints &&
      operation in endpoints &&
      (endpoints as Record<string, unknown>)[operation],
  );

/**
 * Operations that every config-app-backend entity supports implicitly. Entities don't need to
 * declare these in their `operations` list — they're always available.
 */
const IMPLICIT_CONFIG_APP_OPERATIONS: ReadonlyArray<OperationType> = [
  'list',
  'get',
];

const hasOptedIntoOperation = (
  entity: DomainEntityConfiguration,
  operation: OperationType,
): boolean => {
  // Non-CACT-BE entities (`isUsingConfigAppBackend: false`) own their endpoint set explicitly,
  // so capability comes from the endpoints map. CACT-BE entities (flag true or omitted) declare
  // their operations in `operations`, with `list`/`get` always implicit.
  if (entity.isUsingConfigAppBackend === false) {
    return hasEndpointForOperation(entity.endpoints, operation);
  }
  if (IMPLICIT_CONFIG_APP_OPERATIONS.includes(operation)) return true;
  return Boolean(
    entity.operations?.some((entry) => operationId(entry) === operation),
  );
};

/**
 * Single source of truth for the `deepClone` derivation rule: an entity supports `deepClone`
 * whenever it opts into `clone` AND its schema declares relationships. Used by both
 * `hasEntityOperation` (point query) and the build-time endpoint filter so the rule lives in
 * one place.
 *
 * Returns `false` if the entity has no schema — the relationships check requires it.
 */
export const supportsDeepClone = (entity: DomainEntity): boolean => {
  const hasRelationships = Boolean(entity.schema?.properties?.relationships);
  const declaresClone = Boolean(
    entity.operations?.some((entry) => operationId(entry) === 'clone'),
  );
  // Non-CACT-BE entities don't go through the opt-in `operations` list — they own their
  // endpoint map directly — so derive clone capability from the endpoints instead.
  const nonCactBeHasClone =
    entity.isUsingConfigAppBackend === false &&
    hasEndpointForOperation(entity.endpoints, 'clone');

  return hasRelationships && (declaresClone || nonCactBeHasClone);
};

/**
 * Returns whether an entity exposes a given operation. For config-app-backend entities
 * (`isUsingConfigAppBackend` true or omitted), the answer is in the entity's opt-in
 * `operations` list, with `list` and `get` always implicitly available. For non-CACT-BE entities
 * (`isUsingConfigAppBackend: false`), the answer is in the keys of the `endpoints` object.
 *
 * `deepClone` is derived: an entity supports it whenever `clone` is available and the entity's
 * schema declares relationships. Entities don't need to (and don't) declare `deepClone` in
 * their operations list explicitly.
 *
 * Note: deepClone resolution requires the entity's schema to inspect `properties.relationships`.
 * When called with a bare `DomainEntityConfiguration` (no schema — e.g. an `entityConfigs` map
 * entry from generated configs), `deepClone` will always return `false`. Callers that need an
 * accurate deepClone answer must pass the full `DomainEntity`.
 */
export const hasEntityOperation = (
  entity:
    | DomainEntityConfiguration
    | DomainEntity
    | ConfigEntity
    | ConfigEntityConfiguration,
  operation: OperationType,
): boolean => {
  if (isConfigEntity(entity) || isConfigEntityConfiguration(entity)) {
    // ConfigEntity / ConfigEntityConfiguration: capability comes from endpoints map directly.
    return hasEndpointForOperation(entity.endpoints, operation);
  }
  if (operation === 'deepClone') {
    // The deepClone derivation needs schema; only DomainEntity carries one. A bare
    // DomainEntityConfiguration always returns false (documented above).
    if (!isDomainEntity(entity)) return false;
    return supportsDeepClone(entity);
  }
  return hasOptedIntoOperation(entity, operation);
};

/**
 * Returns the object form of an operation entry, or undefined if the operation isn't declared
 * or is declared as a plain string. Use to read `actionLabel` /
 * `requiredEntityMetadataFieldValues` for an operation.
 */
export const getEntityOperationConfig = (
  entity: { operations?: DomainEntityConfiguration['operations'] },
  operation: OperationType,
): EntityOperationConfig | undefined => {
  const entry = entity.operations?.find(
    (item) => operationId(item) === operation,
  );
  return entry && typeof entry === 'object' ? entry : undefined;
};
